import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek } from 'date-fns'
import { tr } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Flag } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { applyTaskFilters, type TaskFilters } from '../../taskFilters'
import type { Project, Task } from '../../types'
import { TASK_STATUS_DOT } from '../../visuals'

type Item = { kind: 'task'; task: Task } | { kind: 'milestone'; id: string; label: string }

const iso = (d: Date) => format(d, 'yyyy-MM-dd')

export function CalendarTab({ project, filters }: { project: Project; filters: TaskFilters }) {
  const { t } = useTranslation()
  const [month, setMonth] = useState(() => startOfMonth(new Date()))

  const byDay = useMemo(() => {
    const map = new Map<string, Item[]>()
    const push = (day: string, item: Item) => map.set(day, [...(map.get(day) ?? []), item])
    for (const task of applyTaskFilters(project.tasks, filters)) push(task.dueDate, { kind: 'task', task })
    for (const i of project.installations) {
      if (filters.installationId === 'all' || filters.installationId === i.id) push(i.targetDate, { kind: 'milestone', id: i.id, label: i.customer })
    }
    return map
  }, [project.tasks, project.installations, filters])

  const days = eachDayOfInterval({ start: startOfWeek(month, { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) })
  const weekdays = days.slice(0, 7).map((d) => format(d, 'EEE', { locale: tr }))
  const monthDays = days.filter((d) => isSameMonth(d, month) && byDay.has(iso(d)))

  const chip = (item: Item, dense: boolean) =>
    item.kind === 'task' ? (
      <Link
        key={item.task.id}
        to={`/projects/${project.id}/tasks/${item.task.id}`}
        className={cn('flex min-w-0 items-center gap-1.5 rounded-md border bg-card px-1.5 py-1 text-xs transition-colors hover:border-primary/40', item.task.status === 'done' && 'text-muted-foreground line-through')}
        title={`${item.task.key} · ${item.task.title}`}
      >
        <span className={cn('size-1.5 shrink-0 rounded-full', TASK_STATUS_DOT[item.task.status])} aria-hidden />
        {dense ? null : <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{item.task.key}</span>}
        <span className="truncate">{item.task.title}</span>
      </Link>
    ) : (
      <Link key={item.id} to={`/projects/${project.id}/installations?i=${item.id}`} className="flex min-w-0 items-center gap-1.5 rounded-md bg-brand-orange/12 px-1.5 py-1 text-xs font-medium text-brand-orange">
        <Flag className="size-3 shrink-0" aria-hidden />
        <span className="truncate">{t('projects.calendar.target', { name: item.label })}</span>
      </Link>
    )

  return (
    <section className="@container overflow-hidden rounded-xl border bg-card shadow-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <h3 className="text-base font-semibold capitalize">{format(month, 'LLLL yyyy', { locale: tr })}</h3>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setMonth(startOfMonth(new Date()))}>
            {t('common.today')}
          </Button>
          <Button variant="ghost" size="icon" className="size-8" onClick={() => setMonth((m) => addMonths(m, -1))} aria-label={t('common.prevMonth')}>
            <ChevronLeft />
          </Button>
          <Button variant="ghost" size="icon" className="size-8" onClick={() => setMonth((m) => addMonths(m, 1))} aria-label={t('common.nextMonth')}>
            <ChevronRight />
          </Button>
        </div>
      </header>

      <div className="hidden @3xl:block">
        <div className="grid grid-cols-7 border-b bg-muted/40 text-xs font-medium uppercase text-muted-foreground">
          {weekdays.map((d) => (
            <div key={d} className="px-2 py-2">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const items = byDay.get(iso(d)) ?? []
            return (
              <div key={iso(d)} className={cn('min-h-28 border-b border-r p-1.5 [&:nth-child(7n)]:border-r-0', !isSameMonth(d, month) && 'bg-muted/30')}>
                <div className={cn('mb-1 flex size-6 items-center justify-center rounded-full text-xs tabular-nums', isToday(d) ? 'bg-primary font-semibold text-primary-foreground' : isSameMonth(d, month) ? 'text-foreground' : 'text-muted-foreground/60')}>
                  {format(d, 'd')}
                </div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((item) => chip(item, true))}
                  {items.length > 3 ? <p className="px-1 text-[11px] text-muted-foreground">{t('projects.calendar.more', { count: items.length - 3 })}</p> : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <ol className="divide-y @3xl:hidden">
        {monthDays.length === 0 ? <li className="px-4 py-10 text-center text-sm text-muted-foreground">{t('projects.calendar.empty')}</li> : null}
        {monthDays.map((d) => (
          <li key={iso(d)} className="flex gap-3 p-3">
            <div className={cn('w-12 shrink-0 text-center', isToday(d) && 'text-primary')}>
              <div className="text-lg font-semibold tabular-nums">{format(d, 'd')}</div>
              <div className="text-[11px] uppercase text-muted-foreground">{format(d, 'EEE', { locale: tr })}</div>
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">{(byDay.get(iso(d)) ?? []).map((item) => chip(item, false))}</div>
          </li>
        ))}
      </ol>
      <p className="border-t px-4 py-2 text-xs text-muted-foreground">{t('projects.calendar.hint')}</p>
    </section>
  )
}
